export class BottomSheet {
  #el; #bodyEl; #backdropEl; #handleEl;
  #snapFracs;
  #onSnap;
  #snap;
  #height;
  #vel;
  #rafId;
  #dragging;
  #dragStartX;
  #dragStartY;
  #dragStartH;
  #startInBody; // gesture began inside the scrollable body
  #axisV;       // true once the gesture is locked to the vertical axis
  #committed;   // true once we've decided this touch is a sheet-drag (not a scroll)
  #velSamples;
  #listeners;

  constructor(el, { snapPoints = [0.2, 0.5, 0.9], onSnap = () => {}, getScrollEl } = {}) {
    this.#el         = el;
    this.#snapFracs  = snapPoints;
    this.#onSnap     = onSnap;
    this.#bodyEl     = getScrollEl?.();
    this.#backdropEl = document.getElementById('stats-drawer-backdrop');
    this.#handleEl   = document.getElementById('stats-drawer-handle');
    this.#snap       = snapPoints[0];
    this.#height     = this.#vh() * snapPoints[0];
    this.#vel        = 0;
    this.#dragging   = false;
    this.#committed  = false;
    this.#axisV      = false;
    this.#velSamples = [];
    this.#listeners  = [];

    this.#applyHeight(this.#height);
    this.#bindEvents();
  }

  #vh() { return window.innerHeight; }
  #maxH() { return this.#vh() * this.#snapFracs[this.#snapFracs.length - 1]; }
  #minH() { return this.#vh() * this.#snapFracs[0]; }

  #applyHeight(h) {
    this.#height = h;
    this.#el.style.height = h + 'px';

    // Interpolate 0→1 from closed snap to max snap
    const t = Math.max(0, Math.min(1, (h - this.#minH()) / (this.#maxH() - this.#minH())));

    // Width: shrinks by 16px when closed (8px each side) → full width when open
    const gap = Math.round(16 * (1 - t));
    this.#el.style.width = `calc(100% - ${gap}px)`;
    this.#el.style.left  = '50%';
    this.#el.style.transform = `translateX(-50%)`;

    // Bottom corners: fully rounded when closed, flat when open
    const br = Math.round(18 * (1 - t));
    this.#el.style.borderRadius = `18px 18px ${br}px ${br}px`;

    // Border ring: visible when closed, fades by 40% open
    const borderA = Math.max(0, (1 - t * 2.5) * 0.13);
    this.#el.style.boxShadow = `0 -4px 24px rgba(0,0,0,0.13), 0 0 0 1px rgba(0,0,0,${borderA})`;

    if (!this.#backdropEl) return;
    const frac = h / this.#vh();
    const half = this.#snapFracs[1] ?? this.#snapFracs[0];
    const top  = this.#snapFracs[this.#snapFracs.length - 1];
    // Dim fades in progressively past the half snap, so the tabs don't look
    // highlighted against a darkened backdrop while the sheet is half-open.
    const p = Math.max(0, Math.min(1, (frac - half) / (top - half)));
    this.#backdropEl.style.background    = `rgba(0,0,0,${(p * 0.32).toFixed(3)})`;
    // Only capture taps (block + tap-to-close) once meaningfully past half,
    // so at peek/half the tabs underneath stay clickable.
    this.#backdropEl.style.pointerEvents = p > 0.12 ? 'auto' : 'none';
  }

  #on(el, event, fn, opts) {
    el.addEventListener(event, fn, opts);
    this.#listeners.push({ el, event, fn });
  }

  // If a spring animation was interrupted by a touch that turned out to be a
  // tap or a horizontal gesture, settle back onto the current snap.
  #resettle() {
    if (!this.#rafId && Math.abs(this.#height - this.#vh() * this.#snap) > 1) {
      this.snapTo(this.#snap);
    }
  }

  #bindEvents() {
    let prevY = null, prevT = null;

    const onStart = (e) => {
      if (this.#dragging) return; // extra finger — the first one keeps control
      if (this.#rafId) { cancelAnimationFrame(this.#rafId); this.#rafId = null; }
      const t = e.touches[0];
      this.#dragStartX  = t.clientX;
      this.#dragStartY  = t.clientY;
      this.#dragStartH  = this.#height;
      this.#startInBody = !!(this.#bodyEl && this.#bodyEl.contains(e.target));
      this.#vel         = 0;
      this.#velSamples  = [];
      this.#dragging    = true;
      this.#committed   = false;
      this.#axisV       = false;
      prevY = t.clientY; prevT = Date.now();
    };

    const onMove = (e) => {
      if (!this.#dragging) return;
      const t   = e.touches[0];
      const y   = t.clientY;
      const now = Date.now();

      // velocity sampling
      if (prevT && now > prevT) {
        this.#velSamples.push((prevY - y) / (now - prevT) * 1000);
        if (this.#velSamples.length > 6) this.#velSamples.shift();
      }
      prevY = y; prevT = now;

      if (!this.#committed) {
        const dy  = this.#dragStartY - y; // + = dragged up (expand)
        const adx = Math.abs(t.clientX - this.#dragStartX);
        const ady = Math.abs(dy);

        // Direction lock (decided once per gesture): horizontal stays native
        // so pans/swipes on charts inside the body aren't hijacked.
        if (!this.#axisV) {
          if (ady < 6 && adx < 6) return; // within slop, undecided
          if (adx > ady) { this.#dragging = false; this.#resettle(); return; }
          this.#axisV = true;
        }

        // At full-open, vertical gestures inside a scrollable body belong to
        // the content: scroll it, and only take over the sheet once it's back
        // at the top and being pulled down.
        const atMax     = this.#height >= this.#maxH() - 2;
        const canScroll = !!(this.#bodyEl && this.#bodyEl.scrollHeight > this.#bodyEl.clientHeight + 1);
        if (atMax && this.#startInBody && canScroll) {
          if (dy > 0) return;                             // scrolling content up
          if ((this.#bodyEl?.scrollTop ?? 0) > 0) return; // not back at top yet
        }

        // Commit: re-baseline so distance already consumed by content
        // scrolling doesn't make the sheet jump.
        this.#committed  = true;
        this.#dragStartY = y;
        this.#dragStartH = this.#height;
      }

      e.preventDefault();

      // Rubber-band resistance beyond snap bounds
      let newH = this.#dragStartH + (this.#dragStartY - y);
      if (newH > this.#maxH()) {
        newH = this.#maxH() + (newH - this.#maxH()) * 0.18;
      } else if (newH < this.#minH() * 0.5) {
        newH = this.#minH() * 0.5;
      }
      this.#applyHeight(newH);
    };

    const onEnd = (e) => {
      if (!this.#dragging) return;
      if (e.touches && e.touches.length > 0) return; // a finger is still down
      this.#dragging = false;
      const committed = this.#committed;
      this.#committed = false;
      prevY = null; prevT = null;
      if (!committed) { this.#resettle(); return; } // tap — nothing to snap
      const recent = this.#velSamples.slice(-4);
      this.#vel = recent.length
        ? recent.reduce((a, b) => a + b, 0) / recent.length
        : 0;
      this.#snapToNearest();
    };

    this.#on(this.#el, 'touchstart',  onStart, { passive: true });
    this.#on(this.#el, 'touchmove',   onMove,  { passive: false });
    this.#on(this.#el, 'touchend',    onEnd,   { passive: true });
    this.#on(this.#el, 'touchcancel', onEnd,   { passive: true });

    if (this.#backdropEl) {
      this.#on(this.#backdropEl, 'click', () => this.snapTo(this.#snapFracs[0]));
    }

    // Tap (or Enter/Space) on the handle cycles peek → half → full → peek
    if (this.#handleEl) {
      const cycle = () => {
        const i = this.#snapFracs.indexOf(this.#snap);
        this.snapTo(this.#snapFracs[(i + 1) % this.#snapFracs.length]);
      };
      this.#on(this.#handleEl, 'click', cycle);
      this.#on(this.#handleEl, 'keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); cycle(); }
      });
    }

    // Viewport changes (rotation, iOS/Android URL bar collapse) invalidate the
    // px height — reapply the current snap against the new viewport.
    const onViewportResize = () => {
      if (this.#dragging) return;
      if (this.#rafId) { cancelAnimationFrame(this.#rafId); this.#rafId = null; }
      this.#vel = 0;
      this.#applyHeight(this.#vh() * this.#snap);
    };
    this.#on(window, 'resize', onViewportResize);
    if (window.visualViewport) {
      this.#on(window.visualViewport, 'resize', onViewportResize);
    }
  }

  #snapToNearest() {
    const frac      = this.#height / this.#vh();
    const threshold = 220; // px/s — lower = easier throw

    let target;
    if (Math.abs(this.#vel) > threshold) {
      if (this.#vel > 0) {
        target = this.#snapFracs.find(s => s > frac + 0.02)
              ?? this.#snapFracs[this.#snapFracs.length - 1];
      } else {
        const rev = [...this.#snapFracs].reverse();
        target = rev.find(s => s < frac - 0.02) ?? this.#snapFracs[0];
      }
    } else {
      target = this.#snapFracs.reduce((best, s) =>
        Math.abs(s - frac) < Math.abs(best - frac) ? s : best
      );
    }

    this.snapTo(target);
  }

  #springAnimate(targetH, onDone) {
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
    const k = 320, damp = 26; // slightly underdamped → iOS-like bounce
    let prev = null;

    const step = (ts) => {
      if (!prev) { prev = ts; this.#rafId = requestAnimationFrame(step); return; }
      const dt    = Math.min((ts - prev) / 1000, 0.032);
      prev        = ts;
      const force = -k * (this.#height - targetH) - damp * this.#vel;
      this.#vel  += force * dt;
      this.#applyHeight(this.#height + this.#vel * dt);

      if (Math.abs(this.#height - targetH) < 0.5 && Math.abs(this.#vel) < 6) {
        this.#applyHeight(targetH);
        this.#vel   = 0;
        this.#rafId = null;
        onDone?.();
        return;
      }
      this.#rafId = requestAnimationFrame(step);
    };

    this.#rafId = requestAnimationFrame(step);
  }

  snapTo(frac) {
    this.#snap = frac;
    this.#applyHeight(this.#height);
    this.#springAnimate(this.#vh() * frac, () => this.#onSnap(frac));
  }

  getCurrentSnap() { return this.#snap; }

  destroy() {
    if (this.#rafId) { cancelAnimationFrame(this.#rafId); this.#rafId = null; }
    for (const { el, event, fn } of this.#listeners) el.removeEventListener(event, fn);
    this.#listeners = [];
    this.#el.style.height       = '';
    this.#el.style.width        = '';
    this.#el.style.left         = '';
    this.#el.style.transform    = '';
    this.#el.style.borderRadius = '';
    this.#el.style.boxShadow    = '';
    if (this.#backdropEl) {
      this.#backdropEl.style.background    = '';
      this.#backdropEl.style.pointerEvents = '';
    }
  }
}
